import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://lognsec.com';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

export type SEOProps = {
  title: string;
  description: string;
  canonical?: string;
  /** Absolute URL or site-root-relative path (e.g. "/logo512.png"). */
  image?: string;
  /** When true, emit noindex so search engines skip the page (e.g. 404). */
  noindex?: boolean;
};

function buildCanonicalUrl(path: string): string {
  const normalized =
    path === '/' || path === ''
      ? '/'
      : path.startsWith('/')
        ? path
        : `/${path}`;
  return `${BASE_URL}${normalized === '/' ? '/' : normalized}`;
}

function buildImageUrl(image?: string): string {
  if (!image) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(image)) return image;
  return `${BASE_URL}${image.startsWith('/') ? image : `/${image}`}`;
}

export function SEO({ title, description, canonical, image, noindex }: SEOProps) {
  const canonicalUrl = canonical !== undefined ? buildCanonicalUrl(canonical) : undefined;
  const imageUrl = buildImageUrl(image);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />

      {/* Open Graph */}
      <meta property="og:site_name" content="logNsec" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={imageUrl} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@lognsec" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
    </Helmet>
  );
}
